import React from 'react';
import { Star, Sparkles, Calendar, AlertTriangle } from 'lucide-react';
import HoroscopeDetails from './HoroscopeDetails';
import { HOROSCOPE_TRANSLATIONS } from '../../../../languages/horoscope';

interface HoroscopeDisplayProps {
    loading: boolean;
    horoscope: any;
    period: string;
    periodInfo: string;
    formatTime?: (time: string) => string;
    language: string;
}

const HoroscopeDisplay: React.FC<HoroscopeDisplayProps> = ({
    loading,
    horoscope,
    period,
    periodInfo,
    language
}) => {
    const langKey = language === 'hindi' ? 'Hindi' : 'English';
    const t = HOROSCOPE_TRANSLATIONS[langKey];

    if (loading) {
        return (
            <div className="h-96 flex flex-col items-center justify-center bg-cosmic-900/50 rounded-3xl border border-white/20 backdrop-blur">
                <div className="relative w-24 h-24 mb-6">
                    <div className="absolute inset-0 rounded-full border-4 border-purple-500/20 animate-pulse"></div>
                    <div className="absolute inset-0 rounded-full border-t-4 border-purple-500 animate-spin"></div>
                    <div className="absolute inset-0 flex items-center justify-center">
                        <Star className="text-purple-400 animate-pulse" size={32} />
                    </div>
                </div>
                <h3 className="text-2xl font-bold text-white mb-2 font-serif">{t.readingTheStars}</h3>
                <p className="text-purple-300/60 max-w-xs text-center font-sans">{t.connectingWithEnergies}</p>
            </div>
        );
    }

    if (!horoscope) {
        return (
            <div className="h-96 flex flex-col items-center justify-center bg-cosmic-900/30 rounded-3xl border border-white/20 border-dashed">
                <div className="text-purple-300/30 text-6xl mb-4">
                    <Star />
                </div>
                <p className="text-purple-300/60 text-lg font-sans">{t.selectSignToReveal}</p>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header Card / Cosmic Overview */}
            <div className="bg-white/[0.12] rounded-[24px] md:rounded-3xl p-5 md:p-8 border border-white/20 relative overflow-hidden group shadow-2xl">
                <div className="absolute top-0 right-0 w-64 h-64 bg-purple-500/10 rounded-full blur-[80px] group-hover:bg-purple-500/20 transition-colors duration-700" />

                <div className="relative z-10">
                    <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-6">
                        <div>
                            <div className="flex flex-wrap items-center gap-3 mb-3 font-sans">
                                <span className="px-3 py-1 bg-purple-500/20 rounded-full text-[10px] font-black text-purple-300 border border-purple-500/30 uppercase tracking-[0.2em] whitespace-nowrap">
                                    {t[period] || period} {t.cosmicForecast}
                                </span>
                                <span className="hidden sm:block w-1.5 h-1.5 rounded-full bg-purple-500/50" />
                                <div className="flex items-center gap-1.5 text-purple-100 font-bold text-sm md:text-base break-words">
                                    <Calendar size={16} className="text-purple-400 flex-shrink-0" />
                                    {periodInfo}
                                </div>
                            </div>
                            <h2 className="text-3xl md:text-5xl font-black text-white leading-tight font-serif">
                                {t.cosmicOverview}
                            </h2>
                        </div>
                    </div>

                    <div className="prose prose-invert prose-purple max-w-none">
                        <p className="text-base md:text-lg text-purple-100/90 leading-relaxed font-sans font-light">
                            {typeof horoscope === 'string' ? horoscope : horoscope.overview}
                        </p>
                    </div>

                    {/* Extra fields based on period */}
                    {(horoscope.major_themes || horoscope.monthly_focus || horoscope.weekly_energy) && (
                        <div className="mt-8 pt-6 border-t border-white/20">
                            <h4 className="text-xs font-bold text-purple-300 uppercase tracking-[0.2em] mb-3 font-sans">
                                {horoscope.major_themes ? t.majorThemes : horoscope.monthly_focus ? t.focusOfMonth : t.weeklyEnergy}
                            </h4>
                            <p className="text-purple-200/70 italic bg-purple-500/5 p-4 rounded-xl border border-purple-500/10 font-sans font-light">
                                "{horoscope.major_themes || horoscope.monthly_focus || horoscope.weekly_energy}"
                            </p>
                        </div>
                    )}
                </div>
            </div>

            {/* Structured Content Grid */}
            {typeof horoscope !== 'string' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Lucky Elements Card */}
                    {horoscope.lucky_elements && (
                        <div className="bg-white/[0.08] backdrop-blur rounded-3xl p-6 border border-white/20 shadow-xl">
                            <h3 className="text-lg font-bold text-white mb-6 flex items-center gap-2 font-serif">
                                <Sparkles className="text-yellow-400" size={20} />
                                {t.luckyElements}
                            </h3>
                            <div className="grid grid-cols-2 gap-4">
                                {Object.entries(horoscope.lucky_elements).map(([key, value]) => (
                                    <div key={key} className="bg-white/[0.08] border border-white/20 rounded-2xl p-4 text-center hover:bg-white/[0.12] transition-colors group">
                                        <span className="text-[10px] text-purple-300/50 uppercase tracking-[0.2em] mb-1 block font-sans font-bold">{t[key.toLowerCase()] || key}</span>
                                        <span className="text-lg font-bold text-white capitalize font-sans">{value as string}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Key Dates & Remedies */}
                    <div className="bg-white/[0.08] backdrop-blur rounded-3xl p-6 border border-white/20 flex flex-col gap-6 shadow-xl">
                        {horoscope.key_dates && (
                            <div>
                                <h3 className="text-xs font-bold text-purple-300 uppercase tracking-[0.2em] mb-3 flex items-center gap-2 font-sans">
                                    <Calendar size={14} /> {t.keyDates}
                                </h3>
                                <div className="flex flex-wrap gap-2">
                                    {Array.isArray(horoscope.key_dates)
                                        ? horoscope.key_dates.map((date: string, i: number) => (
                                            <span key={i} className="px-3 py-1 bg-purple-500/10 border border-purple-500/20 rounded-lg text-xs font-bold text-purple-200 font-sans">
                                                {date}
                                            </span>
                                        ))
                                        : <p className="text-sm text-purple-200/60 font-sans font-light italic">{horoscope.key_dates}</p>
                                    }
                                </div>
                            </div>
                        )}

                        {horoscope.remedies && (
                            <div>
                                <h3 className="text-xs font-bold text-purple-300 uppercase tracking-[0.2em] mb-3 flex items-center gap-2 font-sans">
                                    <Sparkles size={14} /> {t.cosmicRemedies}
                                </h3>
                                <ul className="space-y-2">
                                    {Array.isArray(horoscope.remedies) ? horoscope.remedies.map((remedy: string, i: number) => (
                                        <li key={i} className="text-sm text-purple-200/70 flex items-start gap-2 font-sans font-light">
                                            <span className="mt-1.5 w-1 h-1 rounded-full bg-purple-500 flex-shrink-0" />
                                            {remedy}
                                        </li>
                                    )) : <li className="text-sm text-purple-200/70 font-sans font-light">{horoscope.remedies}</li>}
                                </ul>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Detailed Aspect Analysis */}
            {typeof horoscope !== 'string' && horoscope.aspects && (
                <HoroscopeDetails aspects={horoscope.aspects} language={language} />
            )}

            {/* Warning/Caution Section */}
            {typeof horoscope !== 'string' && horoscope.warning && (
                <div className="bg-red-500/5 backdrop-blur rounded-3xl p-6 border border-red-500/20 flex items-start gap-4 shadow-xl">
                    <div className="w-10 h-10 rounded-full bg-red-500/10 flex items-center justify-center flex-shrink-0">
                        <AlertTriangle size={20} className="text-red-400" />
                    </div>
                    <div>
                        <h3 className="text-lg font-bold text-red-200 mb-2 font-serif">{t.cosmicCaution}</h3>
                        <p className="text-red-200/60 leading-relaxed font-sans font-light italic">
                            {horoscope.warning}
                        </p>
                    </div>
                </div>
            )}

            {/* Additional Period Specific Data */}
            {typeof horoscope !== 'string' && (horoscope.spiritual_guidance || horoscope.challenges_opportunities) && (
                <div className="bg-indigo-900/10 backdrop-blur rounded-[24px] md:rounded-3xl p-6 md:p-8 border border-indigo-500/20 shadow-xl">
                    <h3 className="text-xs font-bold text-indigo-300 mb-4 uppercase tracking-[0.2em] font-sans">
                        {horoscope.spiritual_guidance ? t.spiritualGuidance : t.challengesOpportunities}
                    </h3>
                    <p className="text-indigo-200/70 leading-relaxed text-base md:text-lg italic font-sans font-light">
                        {horoscope.spiritual_guidance || horoscope.challenges_opportunities}
                    </p>
                </div>
            )}
        </div>
    );
};

export default HoroscopeDisplay;
